@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.engineservice.schulung

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.engineservice.support.CUSSupportAbschluss
import org.example.project.engineservice.support.CUSSupportKiChoice
import org.example.project.engineservice.support.CUSSupportLayout
import org.example.project.service.GlobalManager
import org.example.project.service.ZustandManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.events.Event
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

@Composable
fun SchulungsFlow() {
    val scope = rememberCoroutineScope()
    val refreshVersion = GlobalManager.jsonDomainVersion("schulung")
    val requestedSchulungId = window.location.hash
        .substringAfter("?", "")
        .split("&")
        .firstOrNull { it.substringBefore("=").trim() == "schulungId" }
        ?.substringAfter("=")
        ?.let { js("decodeURIComponent")(it) as String }
        .orEmpty()
    var state by remember { mutableStateOf("LOADING") }
    var teilnahme by remember { mutableStateOf<All?>(null) }
    var schulungsDokument by remember { mutableStateOf<All?>(null) }
    var provider by remember { mutableStateOf("") }
    var abschlussbericht by remember { mutableStateOf("") }
    var hint by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(false) }
    var loadedOnce by remember { mutableStateOf(false) }

    fun download(name: String, content: String, mime: String) {
        val isDataUrl = content.startsWith("data:")
        val url = if (isDataUrl) {
            content
        } else {
            URL.createObjectURL(Blob(arrayOf(content), BlobPropertyBag(mime)))
        }
        val anchor = document.createElement("a") as HTMLAnchorElement
        anchor.href = url
        anchor.download = name
        document.body?.appendChild(anchor)
        anchor.click()
        document.body?.removeChild(anchor)
        if (!isDataUrl) URL.revokeObjectURL(url)
    }

    fun chooseProvider(target: String, url: String) {
        val current = teilnahme ?: return
        val documentData = schulungsDokument?.dokumentDataUrl.orEmpty()
        if (documentData.isBlank()) {
            hint = "Das Schulungs-PDF konnte nicht geladen werden."
            return
        }
        provider = target
        download(
            schulungsDokument?.dokumentName.orEmpty().ifBlank { "schulung.pdf" },
            documentData,
            "application/pdf"
        )
        state = "ABSCHLUSS"
        window.open(url, "_blank")
        scope.launch {
            loading = true
            teilnahme = SchulungApi.start(current.schulungId(), target) ?: current
            loading = false
        }
    }

    LaunchedEffect(requestedSchulungId, refreshVersion) {
        if (!loadedOnce) {
            state = "LOADING"
            teilnahme = null
            schulungsDokument = null
            provider = ""
            abschlussbericht = ""
            hint = null
        }
        loading = false
        if (requestedSchulungId.isBlank()) {
            hint = "Keine Schulung ausgewählt."
            state = "ERROR"
            return@LaunchedEffect
        }
        val current = SchulungApi.myTeilnahmen()
            .filter { !it.schulungsModus.equals("PHYSISCH", ignoreCase = true) }
            .firstOrNull { it.schulungId() == requestedSchulungId }
        if (current == null) {
            hint = "Die gewählte Schulung ist diesem Mitarbeiter nicht zugewiesen."
            state = "ERROR"
            return@LaunchedEffect
        }
        teilnahme = current
        schulungsDokument = SchulungApi.document(current.schulungId())
        if (
            current.zustand != ZustandManager.Schulung.ABGESCHLOSSEN &&
            schulungsDokument?.dokumentDataUrl.orEmpty().isBlank()
        ) {
            hint = "Für diese Schulung wurde kein PDF hinterlegt."
            state = "ERROR"
            return@LaunchedEffect
        }
        provider = current.kiAuswahl
        abschlussbericht = current.loesungsweg
        loadedOnce = true
        state = when {
            current.zustand == ZustandManager.Schulung.ABGESCHLOSSEN -> "FERTIG"
            current.zustand >= ZustandManager.Schulung.GESTARTET && provider.isNotBlank() -> "ABSCHLUSS"
            else -> "KI"
        }
    }

    CUSSupportLayout(
        showBackButton = state == "ABSCHLUSS" || state == "FERTIG" || state == "ERROR",
        hint = hint,
        onBack = {
            if (state == "ABSCHLUSS") state = "KI" else window.history.back()
        }
    ) {
        when (state) {
            "LOADING" -> Text("Schulung wird geladen.")
            "KI" -> CUSSupportKiChoice(
                onChatGpt = { chooseProvider("ChatGPT", "https://chatgpt.com") },
                onGemini = { chooseProvider("Gemini", "https://gemini.google.com") },
                onCopilot = { chooseProvider("Copilot", "https://copilot.microsoft.com") }
            )
            "ABSCHLUSS" -> CUSSupportAbschluss(
                abschlussArt = "GELOEST",
                problem = teilnahme?.bezeichnung.orEmpty(),
                loesungsweg = abschlussbericht,
                loading = loading,
                onAbschlussArtChange = {},
                onLoesungswegChange = { abschlussbericht = it },
                onSubmit = {
                    val current = teilnahme ?: return@CUSSupportAbschluss
                    if (abschlussbericht.isBlank()) {
                        hint = "Bitte den Abschlussbericht der KI einfügen."
                        return@CUSSupportAbschluss
                    }
                    loading = true
                    scope.launch {
                        val finished = SchulungApi.finish(
                            current.schulungId(),
                            provider.ifBlank { current.kiAuswahl },
                            abschlussbericht
                        )
                        loading = false
                        if (finished == null) {
                            hint = "Schulung konnte nicht abgeschlossen werden."
                        } else {
                            teilnahme = finished
                            hint = null
                            window.dispatchEvent(Event("lumatica-schulung-updated"))
                            window.history.back()
                        }
                    }
                },
                showOutcomeChoice = false,
                inputPlaceholder = "Abschlussbericht der KI hier einfügen.",
                submitLabel = "Schulung abschließen"
            )
            "FERTIG" -> SchulungFertig(
                bericht = teilnahme?.loesungsweg.orEmpty().ifBlank { abschlussbericht },
                onBack = { window.history.back() }
            )
            "ERROR" -> Unit
        }
    }
}

@Composable
private fun SchulungFertig(bericht: String, onBack: () -> Unit) {
    Div({
        style {
            width(100.percent)
            maxWidth(760.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(16.px)
        }
    }) {
        Span({ style { color(Color("#66BB6A")) } }) { Text("Schulung absolviert") }
        TextArea(attrs = {
            value(bericht)
            attr("readonly", "readonly")
            style(SchulungTextAreaStyle)
        })
        LumaticaButton(
            label = "Zum Kalender",
            onClick = onBack,
            variant = LumaticaButtonVariant.Standard
        )
    }
}

private fun All.schulungId(): String = schulungId.ifBlank { id }
